import React, { useState, useEffect } from "react";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";
import CircularProgress from "@mui/joy/CircularProgress";
import Snackbar from "@mui/joy/Snackbar";
import Drawer from "@mui/joy/Drawer";
import Textarea from "@mui/joy/Textarea";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import { ColorPaletteProp } from "@mui/joy/styles";

import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { gql, useQuery, useMutation } from "@apollo/client";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const TRANSFER_REQUESTS_QUERY = gql`
  query TransferRequests(
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    transferRequests(
      first: $first
      after: $after
      last: $last
      before: $before
    ) {
      edges {
        node {
          id
          state
          createdAt
          reviewedAt
          reason
          supportingDocuments
          asset {
            field01
            field02
            field03
            issueNumber
            controlNumber
            plateNumber
            regNumber
            type
            make
            model
            description
            identificationNumber
            engineNumber
            expiryDate
            owner {
              firstName
              lastName
              email
            }
          }
          requestor {
            firstName
            lastName
            email
          }
          reviewer {
            firstName
            lastName
            email
          }
          originalOwner {
            firstName
            lastName
            email
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const AGREE_TRANSFER_REQUEST_MUTATION = gql`
  mutation AgreeTransferRequest($id: ID!) {
    agreeTransferRequest(input: { id: $id }) {
      transferRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const DISAGREE_TRANSFER_REQUEST_MUTATION = gql`
  mutation DisagreeTransferRequest(
    $id: ID!
    $reason: String!
    $supportingDocuments: [Upload!]!
  ) {
    disagreeTransferRequest(
      input: {
        id: $id
        reason: $reason
        supportingDocuments: $supportingDocuments
      }
    ) {
      transferRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const AGREE_WITHOUT_CONSENT_MUTATION = gql`
  mutation AgreeTransferRequestWithoutConsent(
    $id: ID!
    $reason: String!
    $supportingDocuments: [Upload!]!
  ) {
    agreeTransferRequestWithoutConsent(
      input: {
        id: $id
        reason: $reason
        supportingDocuments: $supportingDocuments
      }
    ) {
      transferRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const DISAGREE_WITHOUT_CONSENT_MUTATION = gql`
  mutation DisagreeTransferRequestWithoutConsent(
    $id: ID!
    $reason: String!
    $supportingDocuments: [Upload!]!
  ) {
    disagreeTransferRequestWithoutConsent(
      input: {
        id: $id
        reason: $reason
        supportingDocuments: $supportingDocuments
      }
    ) {
      transferRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const AGREE_TRANSFER_REQUEST_DISPUTE_MUTATION = gql`
  mutation AgreeTransferRequestDispute($id: ID!) {
    agreeTransferRequestDispute(input: { id: $id }) {
      transferRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const DISAGREE_TRANSFER_REQUEST_DISPUTE_MUTATION = gql`
  mutation DisagreeTransferRequestDispute($id: ID!) {
    disagreeTransferRequestDispute(input: { id: $id }) {
      transferRequest {
        id
        state
        reviewedAt
        reviewer {
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const getFileIcon = (fileName: string) => {
  const extension = fileName.toLowerCase().split(".").pop() || "";
  switch (extension) {
    case "pdf":
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: "danger.500" }} />;
    case "doc":
    case "docx":
      return <DescriptionIcon sx={{ fontSize: 40, color: "primary.500" }} />;
    case "csv":
    case "xls":
    case "xlsx":
      return <TableChartIcon sx={{ fontSize: 40, color: "success.500" }} />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return <ImageIcon sx={{ fontSize: 40, color: "info.500" }} />;
    default:
      return (
        <InsertDriveFileIcon sx={{ fontSize: 40, color: "neutral.500" }} />
      );
  }
};

const getFileIconSmall = (fileName: string) => {
  const extension = fileName.toLowerCase().split(".").pop() || "";
  switch (extension) {
    case "pdf":
      return <PictureAsPdfIcon sx={{ fontSize: 24, color: "danger.500" }} />;
    case "doc":
    case "docx":
      return <DescriptionIcon sx={{ fontSize: 24, color: "primary.500" }} />;
    case "csv":
    case "xls":
    case "xlsx":
      return <TableChartIcon sx={{ fontSize: 24, color: "success.500" }} />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return <ImageIcon sx={{ fontSize: 24, color: "info.500" }} />;
    default:
      return (
        <InsertDriveFileIcon sx={{ fontSize: 24, color: "neutral.500" }} />
      );
  }
};

const isImageFile = (url: string) => {
  const extension = url.toLowerCase().split(".").pop();
  return extension
    ? ["jpg", "jpeg", "png", "gif", "webp"].includes(extension)
    : false;
};

const ReviewDrawer = ({
  open,
  onClose,
  action,
  transferRequest,
}: {
  open: boolean;
  onClose: () => void;
  action: string;
  transferRequest: any;
}) => {
  const [disagreeTransferRequest] = useMutation(
    DISAGREE_TRANSFER_REQUEST_MUTATION,
    {
      refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
    }
  );
  const [agreeWithoutConsent] = useMutation(AGREE_WITHOUT_CONSENT_MUTATION, {
    refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
  });
  const [disagreeWithoutConsent] = useMutation(
    DISAGREE_WITHOUT_CONSENT_MUTATION,
    {
      refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
    }
  );
  const [reason, setReason] = useState("");
  const [supportingDocuments, setSupportingDocuments] = useState<
    Array<{
      file: File;
      id: string;
      name: string;
      size: number;
    }>
  >([]);
  const [dragOver, setDragOver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentError, setDocumentError] = useState("");

  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Check if adding these files would exceed the 5 document limit
    const existingDocumentsCount =
      transferRequest.supportingDocuments?.length || 0;
    const newFilesCount = files.length;
    const totalCount = existingDocumentsCount + newFilesCount;

    if (totalCount > 5) {
      setDocumentError(
        `Cannot add ${newFilesCount} files. Maximum of 5 supporting documents allowed. You can add ${
          5 - existingDocumentsCount
        } more files.`
      );
      return;
    }

    setDocumentError(""); // Clear any previous errors

    // Convert FileList to array and add to existing documents
    const newDocuments = Array.from(files).map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
    }));

    setSupportingDocuments((prev) => [...prev, ...newDocuments]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeFile = (fileId: string) => {
    setSupportingDocuments((prev) => prev.filter((doc) => doc.id !== fileId));
    setDocumentError(""); // Clear error when file is removed
  };

  const isSubmitEnabled =
    reason.trim() !== "" &&
    !documentError &&
    (action === "agree_without_consent" ||
    action === "disagree" ||
    action === "disagree_without_consent"
      ? supportingDocuments.length > 0
      : true);

  const handleSubmit = async () => {
    if (!isSubmitEnabled || isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (action === "disagree") {
        await disagreeTransferRequest({
          variables: {
            id: transferRequest.id,
            reason,
            supportingDocuments: supportingDocuments.map((doc) => doc.file),
          },
        });
      } else if (action === "disagree_without_consent") {
        await disagreeWithoutConsent({
          variables: {
            id: transferRequest.id,
            reason,
            supportingDocuments: supportingDocuments.map((doc) => doc.file),
          },
        });
      } else if (action === "agree_without_consent") {
        await agreeWithoutConsent({
          variables: {
            id: transferRequest.id,
            reason,
            supportingDocuments: supportingDocuments.map((doc) => doc.file),
          },
        });
      }

      onClose();
    } catch (error) {
      console.error("Error submitting review:", error);
      // Check if it's a document limit error
      if (
        error.message &&
        error.message.includes("Maximum of 5 supporting documents")
      ) {
        setDocumentError(
          "Maximum of 5 supporting documents allowed. Please remove some existing documents first."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      size="md"
      sx={{
        "--Drawer-horizontalSize": "500px",
      }}
    >
      <Box
        sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography level="h4" component="h2">
            {action === "disagree"
              ? "Disagree with Transfer Request"
              : action === "agree_without_consent"
              ? "Agree without Consent"
              : action === "disagree_without_consent"
              ? "Disagree without Consent"
              : "Review Transfer Request"}
          </Typography>
          <IconButton onClick={onClose} variant="plain" color="neutral">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography level="body-sm" sx={{ mb: 1, fontWeight: "bold" }}>
              Transfer Request Details
            </Typography>
            <Box
              sx={{ p: 2, bgcolor: "background.level1", borderRadius: "sm" }}
            >
              <Typography level="body-xs">
                <strong>Asset:</strong> {transferRequest?.asset?.plateNumber}
              </Typography>
              <Typography level="body-xs">
                <strong>Requestor:</strong>{" "}
                {transferRequest?.requestor?.firstName}{" "}
                {transferRequest?.requestor?.lastName}
              </Typography>
              <Typography level="body-xs">
                <strong>Original Owner:</strong>{" "}
                {transferRequest?.originalOwner?.firstName}{" "}
                {transferRequest?.originalOwner?.lastName}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography level="body-sm" sx={{ mb: 1, fontWeight: "bold" }}>
              Reason *
            </Typography>
            <textarea
              placeholder={
                action === "disagree"
                  ? "Please provide a reason for disagreeing with this transfer request..."
                  : action === "agree_without_consent"
                  ? "Please provide a reason for approving without owner consent..."
                  : action === "disagree_without_consent"
                  ? "Please provide a reason for disagreeing without owner consent..."
                  : "Please provide a reason..."
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid var(--joy-palette-neutral-300)",
                borderRadius: "6px",
                fontFamily: "inherit",
                fontSize: "14px",
                resize: "vertical",
              }}
            />
          </Box>

          <Box>
            <Typography level="body-sm" sx={{ mb: 1, fontWeight: "bold" }}>
              Supporting Document{" "}
              {action === "agree_without_consent" ||
              action === "disagree" ||
              action === "disagree_without_consent"
                ? "*"
                : "(Optional)"}
            </Typography>
            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
              Maximum 5 documents allowed (
              {transferRequest.supportingDocuments?.length || 0}/5)
              {(action === "agree_without_consent" ||
                action === "disagree" ||
                action === "disagree_without_consent") && (
                <span style={{ color: "var(--joy-palette-danger-500)" }}>
                  {" "}
                  - Required for this action
                </span>
              )}
            </Typography>
            <Box
              sx={{
                border: "2px dashed",
                borderColor: dragOver ? "primary.500" : "neutral.300",
                borderRadius: "sm",
                p: 3,
                textAlign: "center",
                bgcolor: dragOver ? "primary.50" : "background.level1",
                transition: "all 0.2s",
                cursor:
                  (transferRequest.supportingDocuments?.length || 0) >= 5
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  (transferRequest.supportingDocuments?.length || 0) >= 5
                    ? 0.6
                    : 1,
                "&:hover": {
                  borderColor:
                    (transferRequest.supportingDocuments?.length || 0) >= 5
                      ? "neutral.300"
                      : "primary.500",
                  bgcolor:
                    (transferRequest.supportingDocuments?.length || 0) >= 5
                      ? "background.level1"
                      : "primary.50",
                },
              }}
              onDrop={
                (transferRequest.supportingDocuments?.length || 0) >= 5
                  ? undefined
                  : handleDrop
              }
              onDragOver={
                (transferRequest.supportingDocuments?.length || 0) >= 5
                  ? undefined
                  : handleDragOver
              }
              onDragLeave={
                (transferRequest.supportingDocuments?.length || 0) >= 5
                  ? undefined
                  : handleDragLeave
              }
              onClick={() => {
                if ((transferRequest.supportingDocuments?.length || 0) >= 5)
                  return;
                const fileInput = document.getElementById(
                  "file-input"
                ) as HTMLInputElement;
                if (fileInput) fileInput.click();
              }}
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf,.doc,.docx,.csv,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileUpload(e.target.files)}
              />
              <CloudUploadIcon
                sx={{ fontSize: 48, color: "neutral.500", mb: 1 }}
              />
              <Typography level="body-sm" color="neutral">
                {(transferRequest.supportingDocuments?.length || 0) >= 5
                  ? "Maximum documents reached"
                  : "Drag and drop documents here, or click to select multiple files"}
              </Typography>
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                Supported formats: PDF, DOC, DOCX, CSV, XLS, XLSX, TXT, JPG,
                PNG, GIF
              </Typography>
            </Box>
            {documentError && (
              <Typography level="body-xs" color="danger" sx={{ mt: 1 }}>
                {documentError}
              </Typography>
            )}
          </Box>

          {supportingDocuments.length > 0 && (
            <Box>
              <Typography level="body-sm" sx={{ mb: 1, fontWeight: "bold" }}>
                Uploaded Files ({supportingDocuments.length}/5)
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {supportingDocuments.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1,
                      bgcolor: "background.level1",
                      borderRadius: "sm",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {doc.file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(doc.file)}
                          alt={doc.name}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {getFileIcon(doc.name)}
                        </Box>
                      )}
                      <Box>
                        <Typography level="body-xs" sx={{ fontWeight: "bold" }}>
                          {doc.name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {(doc.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => removeFile(doc.id)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color={
              action === "disagree" || action === "disagree_without_consent"
                ? "danger"
                : "warning"
            }
            disabled={!isSubmitEnabled || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting
              ? "Submitting..."
              : action === "disagree"
              ? "Disagree"
              : action === "disagree_without_consent"
              ? "Disagree"
              : "Agree"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

const RowMenu = ({ row }: { row: any }) => {
  const [agreeTransferRequest] = useMutation(AGREE_TRANSFER_REQUEST_MUTATION, {
    refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
  });
  const [disagreeTransferRequest] = useMutation(
    DISAGREE_TRANSFER_REQUEST_MUTATION,
    {
      refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
    }
  );
  const [agreeWithoutConsent] = useMutation(AGREE_WITHOUT_CONSENT_MUTATION, {
    refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
  });
  const [disagreeWithoutConsent] = useMutation(
    DISAGREE_WITHOUT_CONSENT_MUTATION,
    {
      refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
    }
  );
  const [agreeDispute] = useMutation(AGREE_TRANSFER_REQUEST_DISPUTE_MUTATION, {
    refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
  });
  const [disagreeDispute] = useMutation(
    DISAGREE_TRANSFER_REQUEST_DISPUTE_MUTATION,
    {
      refetchQueries: [{ query: TRANSFER_REQUESTS_QUERY }],
    }
  );
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAction, setDrawerAction] = useState("");

  const handleError = (error) => {
    setErrorMessage(error?.message || "An error occurred");
    setOpen(true);
  };

  const handleDrawerOpen = (action) => {
    setDrawerAction(action);
    setDrawerOpen(true);
  };

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: { variant: "plain", color: "neutral", size: "sm" },
          }}
        >
          <MoreHorizRoundedIcon />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          {row.state === "accepted_by_owner" && (
            <>
              <MenuItem
                color="success"
                onClick={() =>
                  agreeTransferRequest({
                    variables: { id: row.id },
                    onError: handleError,
                  })
                }
              >
                Agree
              </MenuItem>
              <MenuItem color="danger" disabled={true}>
                Disagree
              </MenuItem>
            </>
          )}
          {row.state === "rejected_by_owner" && (
            <>
              <MenuItem
                color="danger"
                onClick={() =>
                  agreeTransferRequest({
                    variables: { id: row.id },
                    onError: handleError,
                  })
                }
              >
                Agree with Owner's Rejection
              </MenuItem>
              <MenuItem
                color="success"
                onClick={() => handleDrawerOpen("disagree")}
              >
                Disagree with Owner's Rejection
              </MenuItem>
            </>
          )}
          {row.state === "pending" && (
            <>
              <MenuItem color="success" disabled={true}>
                Agree
              </MenuItem>
              <MenuItem color="danger" disabled={true}>
                Disagree
              </MenuItem>
              <MenuItem
                color="warning"
                onClick={() => handleDrawerOpen("agree_without_consent")}
              >
                Agree without consent
              </MenuItem>
              <MenuItem
                color="danger"
                onClick={() => handleDrawerOpen("disagree_without_consent")}
              >
                Disagree without consent
              </MenuItem>
            </>
          )}
          {row.state === "disputing" && (
            <>
              <MenuItem
                color="danger"
                onClick={() =>
                  disagreeDispute({
                    variables: { id: row.id },
                    onError: handleError,
                  })
                }
              >
                Agree with Original Rejection
              </MenuItem>
              <MenuItem
                color="success"
                onClick={() =>
                  agreeDispute({
                    variables: { id: row.id },
                    onError: handleError,
                  })
                }
              >
                Agree with Disputant and Accept Transfer
              </MenuItem>
            </>
          )}
        </Menu>
      </Dropdown>
      <Snackbar
        open={open}
        color="danger"
        variant="soft"
        onClose={() => setOpen(false)}
        autoHideDuration={3000}
      >
        {errorMessage}
      </Snackbar>
      <ReviewDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        action={drawerAction}
        transferRequest={row}
      />
    </>
  );
};

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr>
        <td>
          <IconButton
            aria-label="expand row"
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar
              size="sm"
              alt={`${row.requestor.firstName} ${row.requestor.lastName}`}
            />
            <div>
              <Typography level="body-xs">
                {row.requestor.firstName} {row.requestor.lastName}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {row.requestor.email}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar
              size="sm"
              alt={`${row.originalOwner.firstName} ${row.originalOwner.lastName}`}
            />
            <div>
              <Typography level="body-xs">
                {row.originalOwner.firstName} {row.originalOwner.lastName}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {row.originalOwner.email}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          <Typography level="body-xs">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(row.createdAt))}
          </Typography>
        </td>
        <td>
          <Chip
            variant="soft"
            size="sm"
            color={
              {
                pending: "warning",
                accepted: "success",
                rejected: "danger",
                accepted_by_owner: "primary",
                rejected_by_owner: "danger",
                disputing: "warning",
              }[row.state] as ColorPaletteProp
            }
          >
            {row.state.replace(/_/g, " ").toUpperCase()}
          </Chip>
        </td>
        <td>
          {row.reviewer && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Avatar
                size="sm"
                alt={`${row.reviewer.firstName} ${row.reviewer.lastName}`}
              />
              <div>
                <Typography level="body-xs">
                  {row.reviewer.firstName} {row.reviewer.lastName}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "200px",
                  }}
                >
                  {row.reviewer.email}
                </Typography>
              </div>
            </Box>
          )}
        </td>
        <td>
          {row.reviewedAt && (
            <Typography level="body-xs">
              {new Intl.DateTimeFormat("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(row.reviewedAt))}
            </Typography>
          )}
        </td>
        <td>
          {[
            "pending",
            "accepted_by_owner",
            "rejected_by_owner",
            "disputing",
          ].includes(row.state) && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <RowMenu row={row} />
            </Box>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8}>
            <Table size="sm">
              <tbody>
                <tr>
                  <td style={{ width: "30%", padding: "8px" }}>
                    <Typography level="body-xs">Field 01</Typography>
                  </td>
                  <td style={{ width: "40%", padding: "8px" }}>
                    <Typography level="body-xs">{row.asset.field01}</Typography>
                  </td>
                  <td
                    rowSpan={12}
                    style={{
                      width: "30%",
                      padding: "8px",
                      verticalAlign: "top",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {row.reason && (
                        <Box>
                          <Typography
                            level="body-xs"
                            sx={{ fontWeight: "bold", mb: 1 }}
                          >
                            Reason:
                          </Typography>
                          <Typography level="body-xs">{row.reason}</Typography>
                        </Box>
                      )}
                      {row.supportingDocuments &&
                        row.supportingDocuments.length > 0 && (
                          <Box>
                            <Typography
                              level="body-xs"
                              sx={{ fontWeight: "bold", mb: 1 }}
                            >
                              Supporting Documents (
                              {row.supportingDocuments.length}):
                            </Typography>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  row.supportingDocuments.length === 1
                                    ? "1fr"
                                    : row.supportingDocuments.length <= 3
                                    ? "repeat(auto-fit, minmax(100px, 1fr))"
                                    : "repeat(auto-fit, minmax(80px, 1fr))",
                                gap: 1,
                                p: 1,
                                bgcolor: "background.level1",
                                borderRadius: "sm",
                                border:
                                  "1px solid var(--joy-palette-neutral-200)",
                                maxWidth:
                                  row.supportingDocuments.length === 1
                                    ? "200px"
                                    : "100%",
                              }}
                            >
                              {row.supportingDocuments.map((url, index) => {
                                const fileName =
                                  url.split("/").pop() ||
                                  `Document ${index + 1}`;
                                return (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 0.5,
                                      p: 1,
                                      bgcolor: "background.surface",
                                      borderRadius: "sm",
                                      border:
                                        "1px solid var(--joy-palette-neutral-200)",
                                      transition: "all 0.2s",
                                      cursor: "pointer",
                                      "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        borderColor: "primary.300",
                                      },
                                    }}
                                    onClick={() => window.open(url, "_blank")}
                                    title={`Click to view ${fileName}`}
                                  >
                                    {isImageFile(url) ? (
                                      <img
                                        src={url}
                                        alt={fileName}
                                        style={{
                                          width: "100%",
                                          height: "60px",
                                          objectFit: "cover",
                                          borderRadius: "4px",
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        sx={{
                                          width: "100%",
                                          height: "60px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          bgcolor: "background.level1",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {getFileIconSmall(fileName)}
                                      </Box>
                                    )}
                                    <Typography
                                      level="body-xs"
                                      sx={{
                                        fontSize: "10px",
                                        textAlign: "center",
                                        color: "neutral.600",
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {fileName.length > 15
                                        ? `${fileName.substring(0, 12)}...`
                                        : fileName}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                    </Box>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 02</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field02}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 03</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field03}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Issue Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.issueNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Control Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.controlNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Plate Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.plateNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Reg Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.regNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Asset Type</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.type}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Make</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.make}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Model</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.model}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Description</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.description}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">
                      Identification Number
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.identificationNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Engine Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.engineNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Expiry Date</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {new Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }).format(new Date(row.asset.expiryDate))}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </Table>
          </td>
        </tr>
      )}
    </>
  );
};

const TransferRequestsTable = () => {
  const [pageSize] = useState(10);
  const { data, loading, error, fetchMore, refetch } = useQuery(
    TRANSFER_REQUESTS_QUERY,
    {
      variables: {
        first: pageSize,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "transfer_request_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.transferRequests.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        transferRequests: {
          ...fetchMoreResult.transferRequests,
          edges: [...fetchMoreResult.transferRequests.edges],
        },
      };
    },
  });

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
              <th style={{ width: 240, padding: "12px 6px" }}>Requestor</th>
              <th style={{ width: 240, padding: "12px 6px" }}>
                Original Owner
              </th>
              <th style={{ width: 160, padding: "12px 6px" }}>Date</th>
              <th style={{ width: 100, padding: "12px 6px" }}>Status</th>
              <th style={{ width: 240, padding: "12px 6px" }}>Reviewer</th>
              <th style={{ width: 160, padding: "12px 6px" }}>Reviewed At</th>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={8}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : (
              data?.transferRequests?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </>
  );
};

const TransferRequests = () => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Transfer Requests
        </Typography>
      </Box>
      <TransferRequestsTable />
    </>
  );
};

export default TransferRequests;
